import type { ExpiryReason } from './SessionTimeout.types';

/**
 * Every `localStorage` touchpoint for the idle/absolute session clock.
 *
 * ## Invariants:
 * - Kept in one module so the keys are shared by the tabs that coordinate through them,
 *   and so the whole surface is testable without a DOM.
 * - Every access is wrapped and falls back to an in-process map. Why: `localStorage`
 *   throws outright in some contexts (Safari private mode, "block site data"), and a
 *   session watchdog must never be the thing that white-screens the app. Losing the
 *   store only costs cross-tab coordination — the tab still expires on its own clock.
 * - Every access is also guarded on `typeof window`. Why: this is a Next.js app and these
 *   helpers are reachable from module scope during a server render. The try/catch alone
 *   would swallow that into a silent fallback-map read rather than surfacing the bug.
 */

const LAST_ACTIVITY_KEY = 'parkingbase:ecommerce:session-last-activity';
const SESSION_START_KEY = 'parkingbase:ecommerce:session-start';
const SIGN_OUT_REASON_KEY = 'parkingbase:ecommerce:session-signed-out-reason';

export const SESSION_STORAGE_KEYS = {
  lastActivity: LAST_ACTIVITY_KEY,
  sessionStart: SESSION_START_KEY,
  signOutReason: SIGN_OUT_REASON_KEY,
};

/** Used only when `localStorage` is unreachable, so the tab still works alone. */
const fallback = new Map<string, string>();

const readRaw = (key: string): string | null => {
  if (typeof window === 'undefined') return null;
  try {
    const value = window.localStorage.getItem(key);
    return value !== null ? value : (fallback.get(key) ?? null);
  } catch {
    return fallback.get(key) ?? null;
  }
};

const writeRaw = (key: string, value: string): void => {
  if (typeof window === 'undefined') return;
  fallback.set(key, value);
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Unavailable; the fallback map above already holds it for this tab.
  }
};

const removeRaw = (key: string): void => {
  if (typeof window === 'undefined') return;
  fallback.delete(key);
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Nothing to do — the value is gone for this tab either way.
  }
};

/** @returns the stored epoch-ms value, or null when absent or not a finite number */
export const readTimestamp = (key: string): number | null => {
  const raw = readRaw(key);
  if (raw === null || raw.trim() === '') return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
};

export const writeTimestamp = (key: string, epochMs: number): void =>
  writeRaw(key, String(epochMs));

export const readLastActivity = (): number | null => readTimestamp(LAST_ACTIVITY_KEY);

export const writeLastActivity = (epochMs: number): void =>
  writeTimestamp(LAST_ACTIVITY_KEY, epochMs);

interface StoredSessionStart {
  startedAt: number;
  userKey: string;
}

/**
 * The start of the absolute window, created once per signed-in session.
 *
 * ## Invariants:
 * - Stamped with `${companyId}::${userId}` rather than a bare user id. Why: this origin is
 *   multi-tenant by path (`/c/{companyId}`), so one browser can hold sessions for two
 *   companies. A record left behind by another customer — or by the same person on another
 *   tenant — is discarded rather than counted against the current session, which would
 *   otherwise expire them the moment they signed in.
 * - A second tab joining an existing session inherits the real start instead of resetting
 *   the window, or the cap would never be reached by anyone who opens tabs.
 */
export const ensureSessionStart = (userKey: string, now: number): number => {
  const raw = readRaw(SESSION_START_KEY);
  if (raw !== null) {
    try {
      const parsed = JSON.parse(raw) as StoredSessionStart;
      if (parsed && Number.isFinite(parsed.startedAt) && parsed.userKey === userKey) {
        return parsed.startedAt;
      }
    } catch {
      // Corrupt record; fall through and replace it.
    }
  }
  const record: StoredSessionStart = { startedAt: now, userKey };
  writeRaw(SESSION_START_KEY, JSON.stringify(record));
  return now;
};

export const writeSignOutReason = (reason: ExpiryReason): void =>
  writeRaw(SIGN_OUT_REASON_KEY, reason);

/**
 * Reads the reason the last session ended and clears it, so the sign-in screen shows the
 * notice once rather than on every future visit.
 */
export const readAndClearSignOutReason = (): ExpiryReason | null => {
  const raw = readRaw(SIGN_OUT_REASON_KEY);
  removeRaw(SIGN_OUT_REASON_KEY);
  if (raw === 'idle' || raw === 'absolute') return raw;
  return null;
};

/**
 * Clears the activity clock and the absolute window. Called when the watcher unmounts,
 * which is what stops a manual sign-out from leaving a stale absolute deadline behind for
 * the next user of this browser. Deliberately leaves the sign-out reason alone — the
 * sign-in screen has not read it yet.
 */
export const clearSessionRecords = (): void => {
  removeRaw(LAST_ACTIVITY_KEY);
  removeRaw(SESSION_START_KEY);
};
